import React, { useEffect, useState, useRef, useContext } from "react";
import { BRAND_MAP_STYLE } from '../utils/mapStyle';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { supabase } from './data/supabaseClient';
import { 
  FaExclamationTriangle, 
  FaCar, 
  FaMapMarkerAlt, 
  FaUser, 
  FaBox,
  FaLocationArrow,
  FaExpandAlt,
  FaFilter,
  FaSlidersH
} from "react-icons/fa";
import { AuthContext } from './Provider';
import { useToast } from './hooks/ToastContext';
import { CustomButton3, CustomButton7 } from './Templates';
import { decodePolyline } from '../utils/polylineUtils';
import Spinner from './common/Spinner';
import "./RouteMapView.css";

/**
 * Component that displays post route and matches on a map
 * 
 * @param {Object} props
 * @param {Object} props.post - The post data
 * @param {Array} props.matches - Array of matching posts/packages
 * @param {Function} props.onViewMatch - Function to view match details
 * @param {Function} props.onMessageUser - Function to message a user
 * @param {boolean} props.showRadiusSelector - Whether to show radius selector
 * @param {number} props.initialRadius - Initial radius in km
 */
const PostRouteMapView = ({ 
  post, 
  matches = [], 
  onViewMatch,
  onMessageUser,
  onFilterChange,
  showRadiusSelector = false,
  initialRadius = 10
}) => {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);
  const mainPolylineRef = useRef(null);
  const infoWindowRef = useRef(null);
  const boundsRef = useRef(null);
  const markerClustererRef = useRef(null);
  
  // State
  const [map, setMap] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [showFullMapView, setShowFullMapView] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedMatchRoute, setSelectedMatchRoute] = useState(null);
  const [filters, setFilters] = useState({
    showDrivers: true,
    showPassengers: true,
    showPackages: true
  });
  const [searchRadius, setSearchRadius] = useState(initialRadius);
  const [maxAllowedRadius, setMaxAllowedRadius] = useState(5); // Default to Free plan radius
  const [mapMarkers, setMapMarkers] = useState({
    startPoint: null,
    endPoint: null,
    matches: []
  });

  // Check user's subscription for max allowed route matching radius
  useEffect(() => {
    if (!user) return;
    
    const checkSubscriptionLimits = async () => {
      try {
        const { data: limitData, error: limitError } = await supabase.rpc('check_subscription_limit', {
          user_id: user.id,
          feature_name: 'route_match_radius_km'
        });
        
        if (limitError) {
          console.error('Error checking subscription limits:', limitError);
          return;
        }
        
        if (limitData?.value) {
          const radius = parseInt(limitData.value, 10);
          setMaxAllowedRadius(radius);
          
          // If current radius exceeds allowed limit, reset to max allowed
          if (searchRadius > radius) {
            setSearchRadius(radius);
            addToast({
              type: 'info',
              title: t('subscription.limit_applied') || 'Subscription Limit Applied',
              description: `Your current plan allows maximum ${radius}km matching radius.`
            });
          }
        }
      } catch (error) {
        console.error('Error checking subscription limits:', error);
      }
    };
    
    checkSubscriptionLimits();
  }, [user, addToast, t, searchRadius]);

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setIsMapLoading(false);
      } else {
        console.warn("Google Maps API not loaded yet, waiting...");
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  // Clean up markers and polylines when component unmounts
  useEffect(() => {
    return () => {
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      // Clear marker clusterer
      if (markerClustererRef.current) {
        markerClustererRef.current.clearMarkers();
        markerClustererRef.current = null;
      }
      
      // Clear all markers
      clearAllMarkers();
    };
  }, []);
  
  // Helper to clear all markers and polylines
  const clearAllMarkers = () => {
    // Clear marker clusterer first
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
    }
    
    // Clear start/end markers (if they exist)
    if (mapMarkers.startPoint) {
      mapMarkers.startPoint.setMap(null);
    }
    
    if (mapMarkers.endPoint) {
      mapMarkers.endPoint.setMap(null);
    }
    
    // Clear match markers
    mapMarkers.matches.forEach(marker => {
      if (marker) marker.setMap(null);
    });
    
    // Clear all polylines
    polylinesRef.current.forEach(polyline => {
      if (polyline) polyline.setMap(null);
    });
    
    // Clear main route polyline
    if (mainPolylineRef.current) {
      mainPolylineRef.current.setMap(null);
      mainPolylineRef.current = null;
    }
    
    // Reset the markers state
    setMapMarkers({
      startPoint: null,
      endPoint: null,
      matches: []
    });
    
    // Clear the polylines ref
    polylinesRef.current = [];
  };

  // Initialize map
  useEffect(() => {
    if (isMapLoading || mapInitialized || !mapRef.current || !post) return;

    try {
      console.log("Initializing Google Maps for post route map view...");

      // Default to Latvia if no coordinates
      let defaultLocation = { lat: 56.9677, lng: 24.1056 };
      
      // Create map instance first
      const mapOptions = {
        center: defaultLocation,
        zoom: 9,
        fullscreenControl: false,
        mapTypeControl: true,
        streetViewControl: false,
        styles: BRAND_MAP_STYLE
      };
      
      const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
      setMap(newMap);
      
      // Initialize bounds for auto-zooming
      boundsRef.current = new window.google.maps.LatLngBounds();
      
      // Initialize marker clusterer with custom styling and click handling
      const clustererOptions = {
        gridSize: 60,
        maxZoom: 15,
        minimumClusterSize: 2,
        styles: [
          {
            url: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="18" fill="${getComputedStyle(document.documentElement).getPropertyValue('--accent-color2').trim() || '#5a9dc9'}" stroke="white" stroke-width="2" opacity="0.8"/>
                <text x="20" y="26" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="12" font-weight="bold">LABEL_TEXT</text>
              </svg>
            `),
            width: 40,
            height: 40,
            textColor: 'white',
            textSize: 12
          },
          {
            url: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                <circle cx="25" cy="25" r="23" fill="${getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim() || '#9FC131'}" stroke="white" stroke-width="2" opacity="0.8"/>
                <text x="25" y="31" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="14" font-weight="bold">LABEL_TEXT</text>
              </svg>
            `),
            width: 50,
            height: 50,
            textColor: 'white',
            textSize: 14
          },
          {
            url: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="28" fill="${getComputedStyle(document.documentElement).getPropertyValue('--main-color').trim() || '#313a3a'}" stroke="white" stroke-width="3" opacity="0.9"/>
                <text x="30" y="37" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="16" font-weight="bold">LABEL_TEXT</text>
              </svg>
            `),
            width: 60,
            height: 60,
            textColor: 'white',
            textSize: 16
          }
        ]
      };
      
      markerClustererRef.current = new MarkerClusterer({
        map: newMap,
        ...clustererOptions
      });
      
      console.log('MarkerClusterer initialized successfully');
      
      setMapInitialized(true);
      console.log("Map and marker clusterer initialized successfully for post route map view");
    } catch (e) {
      console.error("Error initializing map:", e);
      setError(t('errors.map_initialization_failed'));
    }
  }, [isMapLoading, post, t]);
  
  // Add a click listener to the map to remove the route when clicking elsewhere
  useEffect(() => {
    if (!map) return;
    
    // Add click listener to map
    const clickListener = map.addListener('click', () => {
      console.log('Map clicked, clearing all routes');
      // Remove any existing selected match route using our clearSelectedRoute function
      clearSelectedRoute();
      
      // Close any open info window
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      
      // Additionally, clear all polylines as a fail-safe
      polylinesRef.current.forEach(polyline => {
        if (polyline) polyline.setMap(null);
      });
      polylinesRef.current = [];
    });
    
    // Clean up listener when component unmounts
    return () => {
      if (clickListener) {
        window.google.maps.event.removeListener(clickListener);
      }
    };
  }, [map]);

  // Draw post route when map is initialized
  useEffect(() => {
    if (!mapInitialized || !map || !post) return;
    
    // Clear previous markers/routes
    clearAllMarkers();
    
    console.log("Drawing post route on map");
    
    // Get route points from the polyline
    const routePolyline = post.route_polyline;
    if (!routePolyline) {
      console.error("No route polyline found for post");
      setError(t('errors.no_route_data'));
      return;
    }
    
    const path = decodePolyline(routePolyline);
    if (path.length < 2) {
      console.error("Not enough points in polyline");
      setError(t('errors.invalid_route_data'));
      return;
    }
    
    console.log(`Decoded ${path.length} points from polyline`);
    
    // Draw the route polyline
    const polyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: "#0066CC",
      strokeOpacity: 0.8,
      strokeWeight: 5
    });
    
    polyline.setMap(map);
    mainPolylineRef.current = polyline;
    
    // Update the markers state (no start/end markers)
    setMapMarkers(prev => ({
      ...prev,
      startPoint: null,
      endPoint: null
    }));
    
    // Add points to bounds
    path.forEach(point => {
      boundsRef.current.extend(new window.google.maps.LatLng(
        point.lat,
        point.lng
      ));
    });
    
    // Fit map to bounds
    if (!boundsRef.current.isEmpty()) {
      map.fitBounds(boundsRef.current);
    }
  }, [mapInitialized, map, post, t]);
  
  // Add match markers to map when matches change
  useEffect(() => {
    if (!mapInitialized || !map || !markerClustererRef.current) return;

    // Always clear previous match markers from clusterer
    markerClustererRef.current.clearMarkers();

    // Clear any selected match routes when matches change
    if (selectedMatchRoute) {
      selectedMatchRoute.setMap(null);
      setSelectedMatchRoute(null);
    }

    // Clear all polylines
    polylinesRef.current.forEach(polyline => {
      if (polyline) polyline.setMap(null);
    });
    polylinesRef.current = [];

    // If no matches, just clear and return
    if (!matches || matches.length === 0) {
      console.log('No matches to display, markers cleared');
      setMapMarkers(prev => ({
        ...prev,
        matches: []
      }));
      return;
    }

    // Set up new match markers
    const newMatchMarkers = [];
    console.log(`Adding ${matches.length} match markers to clustered map`);
    
    // Filter matches based on filters
    const filteredMatches = matches.filter(match => {
      if (match.match_role === 'Driver' && !filters.showDrivers) return false;
      if (match.match_role === 'Passenger' && !filters.showPassengers) return false;
      if (match.match_role === 'Package' && !filters.showPackages) return false;
      return true;
    });
    
    // Process each match
    filteredMatches.forEach(match => {
      try {
        // Create point from match coordinates
        if (!match.start_point_lat || !match.start_point_lng) {
          console.warn("Match missing start coordinates:", match);
          return;
        }
        
        // Create position object
        const position = {
          lat: parseFloat(match.start_point_lat),
          lng: parseFloat(match.start_point_lng)
        };
        
        // Determine marker color based on role
        let iconUrl;
        if (match.match_role === 'Driver') {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";
        } else if (match.match_role === 'Passenger') {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/purple-dot.png";
        } else if (match.match_role === 'Package') {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
        } else {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/orange-dot.png";
        }
        
        // Create marker (note: don't set map here, clusterer will handle it)
        const matchMarker = new window.google.maps.Marker({
          position,
          title: `${match.username} - ${match.match_role}`,
          icon: {
            url: iconUrl,
            scaledSize: new window.google.maps.Size(32, 32)
          },
          zIndex: 500,
          // Store match data with marker
          matchData: match
        });
        
        // Add click listener to marker - simplified to just draw route and notify parent
        matchMarker.addListener('click', function() {
          // Clear ALL previous routes including any polylines
          polylinesRef.current.forEach(polyline => {
            if (polyline) polyline.setMap(null);
          });
          polylinesRef.current = [];
          
          if (selectedMatchRoute) {
            selectedMatchRoute.setMap(null);
            setSelectedMatchRoute(null);
          }
          
          // Set this as the new selected match
          setSelectedMatch(match);
          
          // Always display this match's route
          fetchAndDisplayMatchRoute(match);
          
          // Send the match to the parent component for display
          if (onViewMatch) {
            onViewMatch(match);
          }
        });
        
        // Add marker to list
        newMatchMarkers.push(matchMarker);
        
        // Add point to bounds
        boundsRef.current.extend(position);
      } catch (e) {
        console.error("Error creating match marker:", e);
      }
    });
    
    // Add all markers to the clusterer at once
    if (newMatchMarkers.length > 0) {
      markerClustererRef.current.addMarkers(newMatchMarkers);
      console.log(`Added ${newMatchMarkers.length} markers to clusterer`);
    }
    
    // Update the markers state
    setMapMarkers(prev => ({
      ...prev,
      matches: newMatchMarkers
    }));
    
    // Refit bounds if we have any matches
    if (newMatchMarkers.length > 0 && !boundsRef.current.isEmpty()) {
      map.fitBounds(boundsRef.current);
    }
  }, [mapInitialized, map, matches, onViewMatch, onMessageUser, t, filters]);
  
  // Function to clear the selected match route
  const clearSelectedRoute = () => {
    console.log('Clearing all routes');
    
    // Clear the selected match route
    if (selectedMatchRoute) {
      selectedMatchRoute.setMap(null);
      setSelectedMatchRoute(null);
    }
    
    // Clear ALL polylines
    polylinesRef.current.forEach(polyline => {
      if (polyline) polyline.setMap(null);
    });
    polylinesRef.current = [];
    
    setSelectedMatch(null);
  };
  
  // Direct function to fetch and display a match's route (no toggling)
  const fetchAndDisplayMatchRoute = (match) => {
    if (!match) return;
    
    // Clear any existing selected match route
    if (selectedMatchRoute) {
      selectedMatchRoute.setMap(null);
    }
    
    // Get match route polyline from database
    fetchMatchRoutePolyline(match)
      .then(polyline => {
        if (!polyline) {
          console.warn("No polyline found for match");
          addToast({
            type: 'warning',
            title: t('posts.no_route_data'),
            description: t('posts.no_route_data_message')
          });
          return;
        }
        
        // Decode the polyline and draw it
        const path = decodePolyline(polyline);
        if (path.length < 2) {
          console.warn("Not enough points in match polyline");
          return;
        }
        
        // Determine color based on role
        let strokeColor;
        if (match.match_role === 'Driver') {
          strokeColor = "#4285F4"; // Blue
        } else if (match.match_role === 'Passenger') {
          strokeColor = "#9C27B0"; // Purple  
        } else if (match.match_role === 'Package') {
          strokeColor = "#FBBC05"; // Yellow
        } else {
          strokeColor = "#FF5722"; // Orange
        }
        
        // Create the polyline
        const matchPolyline = new window.google.maps.Polyline({
          path,
          geodesic: true,
          strokeColor,
          strokeOpacity: 0.7,
          strokeWeight: 4,
          zIndex: 400
        });
        
        matchPolyline.setMap(map);
        setSelectedMatchRoute(matchPolyline);
        
        // Store the polyline in the reference array for later cleanup
        polylinesRef.current.push(matchPolyline);
        
        // Update bounds to include this route
        const newBounds = new window.google.maps.LatLngBounds();
        
        // Include main route points
        if (mainPolylineRef.current) {
          mainPolylineRef.current.getPath().forEach(point => {
            newBounds.extend(point);
          });
        }
        
        // Include match route points
        path.forEach(point => {
          newBounds.extend(point);
        });
        
        // Update the map bounds
        if (!newBounds.isEmpty()) {
          map.fitBounds(newBounds);
        }
      })
      .catch(error => {
        console.error("Error fetching match route:", error);
        addToast({
          type: 'error',
          title: t('errors.error'),
          description: t('errors.route_fetch_failed')
        });
      });
  };
  
  // Fetch a match's route polyline from the database
  const fetchMatchRoutePolyline = async (match) => {
    try {
      if (!match || !match.match_id || !match.match_type) {
        throw new Error("Invalid match data");
      }
      
      console.log('Fetching route polyline for match:', match);
      
      // Determine the table based on match type
      let table, idColumn;
      if (match.match_type === 'post') {
        table = 'posts';
        idColumn = 'id';
      } else if (match.match_type === 'package') {
        table = 'packages';
        idColumn = 'id';
      } else {
        throw new Error("Unsupported match type: " + match.match_type);
      }
      
      // Supabase query to get the polyline
      const { data, error } = await supabase
        .from(table)
        .select('route_polyline')
        .eq(idColumn, match.match_id)
        .single();
      
      if (error) {
        throw error;
      }
      
      const polyline = data?.route_polyline || null;
      console.log('Fetched polyline:', polyline ? 'Found' : 'Not found');
      
      // For all post types (driver, passenger, package), use the stored polyline if available
      if (polyline) {
        return polyline;
      }
      
      // If no polyline is found in the database, show a user-friendly error
      console.error('No route polyline found for match ID:', match.match_id);
      addToast({
        type: 'warning',
        title: t('posts.no_route_data'),
        description: t('posts.match_no_route_message') || 'This match does not have a stored route. Please check the post details.'
      });
      
      // Important: Return null instead of creating a fallback line
      // This prevents drawing an incorrect route
      return null;
    } catch (error) {
      console.error("Error fetching match route polyline:", error);
      throw error;
    }
  };

  // Toggle full view
  const toggleFullView = () => {
    setShowFullMapView(!showFullMapView);
    
    // Trigger resize event to ensure map renders correctly
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      
      // Re-center map
      recenterMap();
    }, 300);
  };

  // Recenter map function
  const recenterMap = () => {
    if (!map || !boundsRef.current || boundsRef.current.isEmpty()) return;
    
    try {
      map.fitBounds(boundsRef.current);
    } catch (e) {
      console.error("Error re-centering map:", e);
    }
  };

  // Handle radius change with subscription limit checking
  const handleRadiusChange = (e) => {
    const newRadius = parseInt(e.target.value, 10);
    
    // Check if new radius exceeds subscription limit
    if (newRadius > maxAllowedRadius) {
      addToast({
        type: 'warning',
        title: t('errors.limit_reached') || 'Subscription Limit',
        description: `Your current plan allows maximum ${maxAllowedRadius}km matching radius. Upgrade for wider search range!`
      });
      
      // Ask if they want to view subscription plans
      if (window.confirm(t('chat.view_subscription_plans') || 'Would you like to view our subscription plans?')) {
        navigate('/subscription-plans');
        return;
      }
      
      // Set to max allowed if user declines to view plans
      setSearchRadius(maxAllowedRadius);
      return;
    }
    
    // Proceed with the update if within limits
    setSearchRadius(newRadius);
    
    // Update post in parent component if possible
    if (post && post.id) {
      const updatePostRadius = async () => {
        try {
          await supabase
            .from(post.isPackage ? 'packages' : 'posts')
            .update({ search_radius_km: newRadius })
            .eq('id', post.id);
          
          console.log(`Updated search radius to ${newRadius}km for ${post.isPackage ? 'package' : 'post'} ID: ${post.id}`);
          
          // Refresh matches if needed
          if (onFilterChange) {
            onFilterChange({ searchRadius: newRadius });
            
            // Dispatch an event to trigger a refresh
            const refreshEvent = new CustomEvent('refreshMatches', {
              detail: { radius: newRadius }
            });
            window.dispatchEvent(refreshEvent);
            
            // Display a toast to indicate changes are being applied
            addToast({
              type: 'info',
              title: t('matches.radius_updated') || 'Search Radius Updated',
              description: t('matches.refreshing_matches') || 'Refreshing matches with new radius...'
            });
          }
        } catch (error) {
          console.error('Error updating search radius:', error);
          addToast({
            type: 'error',
            title: t('errors.update_failed') || 'Update Failed',
            description: t('errors.radius_update_failed') || 'Failed to update search radius.'
          });
        }
      };
      
      updatePostRadius();
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (filterType) => {
    const newFilters = {
      ...filters,
      [filterType]: !filters[filterType]
    };
    
    setFilters(newFilters);
    
    // Notify parent component if needed
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  // Display loading state
  if (isMapLoading) {
    return (
      <div className="route-map-loading">
        <Spinner size="medium" customText={t('app.loading_map')} />
      </div>
    );
  }

  // Display error state
  if (error) {
    return (
      <div className="route-map-error">
        <FaExclamationTriangle />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={`route-map-view ${showFullMapView ? 'full-view' : ''}`}>
      <div className="route-map-header">
        <h3>
          <FaMapMarkerAlt /> {t('posts.route_map')}
        </h3>
        <div className="route-map-actions">
          {showRadiusSelector && (
            <div className="radius-control">
              <select value={searchRadius} onChange={handleRadiusChange} title={t('matches.search_radius') || 'Search Radius'}>
                <option value="5">5 km</option>
                <option value="10" disabled={maxAllowedRadius < 10}>10 km</option>
                <option value="15" disabled={maxAllowedRadius < 15}>15 km</option>
                <option value="20" disabled={maxAllowedRadius < 20}>20 km</option>
                <option value="30" disabled={maxAllowedRadius < 30}>30 km</option>
              </select>
            </div>
          )}
          <button 
            className="recenter-button map-action-button" 
            onClick={recenterMap}
            title={t('posts.recenter_map')}
          >
            <FaLocationArrow />
          </button>
          <button 
            className="expand-button map-action-button" 
            onClick={toggleFullView}
            title={showFullMapView ? t('posts.minimize_map') : t('posts.maximize_map')}
          >
            <FaExpandAlt />
          </button>
          <button 
            className="filter-button map-action-button" 
            onClick={() => document.getElementById(`filter-dropdown-${post.id}`).classList.toggle("show")}
            title={t('posts.filter_matches')}
          >
            <FaFilter />
          </button>
          <div id={`filter-dropdown-${post.id}`} className="filter-dropdown">
            <div className="filter-header">
              <FaSlidersH /> {t('posts.filter_matches')}
            </div>
            <div className="filter-item">
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.showDrivers} 
                  onChange={() => handleFilterChange('showDrivers')} 
                />
                <div className="filter-color driver"></div>
                {t('posts.show_drivers')}
              </label>
            </div>
            <div className="filter-item">
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.showPassengers} 
                  onChange={() => handleFilterChange('showPassengers')} 
                />
                <div className="filter-color passenger"></div>
                {t('posts.show_passengers')}
              </label>
            </div>
            <div className="filter-item">
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.showPackages} 
                  onChange={() => handleFilterChange('showPackages')} 
                />
                <div className="filter-color package"></div>
                {t('posts.show_packages')}
              </label>
            </div>
          </div>
        </div>
      </div>
      
      <div className="route-map-container" ref={mapRef} style={{ height: '400px' }}></div>
      
      {showFullMapView && (
        <div className="close-fullview-button">
          <CustomButton3
            label={t('buttons.close')}
            onClick={toggleFullView}
          />
        </div>
      )}
    </div>
  );
};

export default PostRouteMapView;